import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { usePortal, type SectionStatus } from "../PortalShell";
import { sectionToApiCode } from "../portal-flow";
import { RevertChangesDialog } from "../RevertChangesDialog";
import { usePortalBootstrap, flowFlags, portalQueries, formatDutchDate } from "@/client/lib/portal";
import { invalidateSchool } from "@/client/lib/query";
import { buildAvailabilityLines, AvailabilityAlerts } from "@/client/pages/school/OverviewPage";
import { Button } from "@/client/components/ui/button";
import { Checkbox } from "@/client/components/ui/checkbox";
import { Notice } from "@/client/components/ui/notice";
import { CheckIcon, AlertCircleIcon, CircleDotIcon, ArrowRightIcon, PencilIcon, ClockIcon, TriangleAlert, RotateCcwIcon } from "lucide-react";

type OverviewLite = {
  availability?: Array<{ key: string; label: string; state: "yes" | "no" | "later" | "geen" | "empty"; entries: string[] }>;
  groups?: Array<{ id: number; displayName: string; yearLevels: Array<{ code: string; label: string; studentCount: number }>; totalStudents: number; vrijeDagdelenLater: boolean; vrijeDagdelenGeen: boolean; vakdocentLater: boolean; vakdocentGeen: boolean }>;
};

function formatSubmittedAt(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
  const time = d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  return `${date} om ${time}`;
}

export function ControlerenSection({ schoolId }: { schoolId: number }) {
  void schoolId;
  const { sections, schoolId: scId, refreshSections } = usePortal();
  const confirmId = useId();
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<"generic" | "no_changes" | null>(null);
  // The open revert confirm dialog: null = closed. `scope` "flow" reverts the whole aanlevering;
  // "section" reverts a single section (its server codes). `sectionCodes` undefined = whole flow.
  const [revertTarget, setRevertTarget] = useState<{ scope: "section" | "flow"; sectionCodes?: string[] } | null>(null);

  // Flow state + deadline come from the shared portal bootstrap (cached), so this page
  // renders the right branch on first paint instead of fetching its own status as a late
  // effect and swapping the UI. `flow === null` means the bootstrap is still loading.
  const { data: bootstrap } = usePortalBootstrap(scId);
  const flow = bootstrap ? flowFlags(bootstrap.flows.schoolgegevens) : null;
  const planningStarted = bootstrap?.planningStarted ?? false;
  // The app-wide planning deadline, formatted for the "aangeleverd" cards (empty when unset).
  const deadlineNice = formatDutchDate(bootstrap?.planningDeadline);

  // Compact beschikbaarheid summary, mirroring the Overzicht: a school-wide Studiedagen status
  // line plus one count line per per-group type ("X opgegeven, Y geen, Z later (van N groepen)"),
  // instead of a long per-group enumeration. Read from the prefetched overview cache (the portal
  // preflight warms it); when it is not yet there we simply show nothing extra.
  const { data: overviewData } = useQuery(portalQueries.overview(scId));
  const overview = overviewData as OverviewLite | undefined;
  const availabilityLines = overview?.groups
    ? buildAvailabilityLines(overview.availability ?? [], overview.groups)
    : [];

  const prev = sections.filter((s) => s.code !== "controleren");
  const allDone = prev.every((s) => s.status === "done");
  const done = (st: SectionStatus) => st === "done";

  // Once handed in with no further edits, there is nothing to submit again.
  const alreadyDelivered = !!flow?.everSubmitted && !flow.hasUnsubmittedChanges;
  const hasChanges = !!flow?.everSubmitted && flow.hasUnsubmittedChanges;
  const canSubmit = confirmed && !alreadyDelivered;

  // After a revert: invalidate the school cache (bootstrap/overview drive the flow + per-section
  // delivery state) and re-pull the stepper so the reverted section/flow flips back to green.
  const handleReverted = async () => {
    invalidateSchool(scId);
    await refreshSections();
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Freeze a full version snapshot of the whole aanlevering (gegevens flow).
      const res = await fetch(`/api/school/${scId}/submit-version`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ flow: "schoolgegevens", confirmed: true }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (res.status === 409 && body?.error === "no_changes") {
          setSubmitError("no_changes");
          invalidateSchool(scId);
          refreshSections();
        } else {
          setSubmitError("generic");
        }
        setSubmitting(false);
        return;
      }
      setJustSubmitted(true);
      invalidateSchool(scId);
      // The left-rail stepper is built from a manual /progress fetch, not a query observer,
      // so invalidate alone won't refresh it — re-pull so "Controleren" flips to done.
      refreshSections();
    } catch {
      setSubmitError("generic");
      setSubmitting(false);
    }
  };

  // ── Just submitted: brief thank-you with onward CTA ──
  if (justSubmitted) {
    return (
      <DeliveredCard
        scId={scId}
        title="Gegevens aangeleverd"
        body={
          deadlineNice
            ? `Bedankt. KUVO neemt uw gegevens door. U kunt nu verder met het programma. Klopt er later iets niet? U kunt uw gegevens altijd aanpassen en opnieuw aanleveren, het liefst wel voor de planningsdeadline van ${deadlineNice}.`
            : "Bedankt. KUVO neemt uw gegevens door. U kunt nu verder met het programma. Klopt er later iets niet? U kunt uw gegevens altijd aanpassen en opnieuw aanleveren."
        }
      />
    );
  }

  // Submission status not known yet: render a calm placeholder, never the review UI.
  // "Unknown" is not "not submitted": rendering the review/submit view here and then
  // swapping to the "aangeleverd" card once the status arrives is the flash we want gone.
  if (flow === null) {
    return (
      <div className="space-y-3" aria-hidden="true">
        <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-28 animate-pulse rounded-lg bg-muted" />
        <div className="h-9 w-44 animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  // ── Already delivered, nothing changed since: no submit button ──
  if (allDone && alreadyDelivered) {
    // Closing sentence about re-aanleveren, with the planning deadline appended when set.
    const reLever = deadlineNice
      ? `daarna kunt u opnieuw aanleveren, het liefst wel voor de planningsdeadline van ${deadlineNice}.`
      : "daarna kunt u opnieuw aanleveren.";
    return (
      <DeliveredCard
        scId={scId}
        title="Gegevens aangeleverd"
        body={
          flow?.lastSubmittedAt
            ? `Aangeleverd op ${formatSubmittedAt(flow.lastSubmittedAt)}. Er zijn sindsdien geen wijzigingen, dus u hoeft niets opnieuw aan te leveren. Wilt u iets aanpassen? Wijzig de gegevens in de stappen hiernaast; ${reLever}`
            : `U heeft uw gegevens aangeleverd. Wilt u iets aanpassen? Wijzig de gegevens in de stappen hiernaast; ${reLever}`
        }
      />
    );
  }

  return (
    <div>
      <p className="text-sm leading-6 text-muted-foreground">
        Controleer of alles klopt voordat u de gegevens aanlevert bij KUVO.
      </p>

      {/* Section summary cards */}
      <div className="mt-4 divide-y divide-border rounded-lg border shadow-xs">
        {prev.map((s) => {
          // A section is "Gewijzigd" when its content no longer matches the last aanlevering.
          // Only then offer a per-section "Wijzigingen terugzetten" (and only once a version exists).
          const changed = !!flow?.everSubmitted && s.deliveryState === "changed";
          return (
            <div key={s.code} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{s.title}</p>
                {s.summary && <p className="mt-0.5 text-xs text-muted-foreground">{s.summary}</p>}
                {changed && (
                  <button
                    type="button"
                    onClick={() => setRevertTarget({ scope: "section", sectionCodes: [sectionToApiCode(s.code)] })}
                    className="mt-1.5 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
                  >
                    <RotateCcwIcon aria-hidden size={12} className="opacity-70" />
                    Wijzigingen terugzetten
                  </button>
                )}
              </div>
              {s.pendingLater ? (
                <span className="inline-flex shrink-0 items-center gap-1.5 text-sm text-amber-600">
                  <ClockIcon size={14} className="opacity-70" /> Wordt later ingevuld
                </span>
              ) : changed ? (
                <span className="inline-flex shrink-0 items-center gap-1.5 text-sm text-amber-600">
                  <PencilIcon size={14} className="opacity-60" /> Gewijzigd
                </span>
              ) : done(s.status) ? (
                <span className="inline-flex shrink-0 items-center gap-1.5 text-sm text-emerald-600">
                  <CheckIcon size={14} className="opacity-60" /> Afgerond
                </span>
              ) : (
                <span className="inline-flex shrink-0 items-center gap-1.5 text-sm text-red-600">
                  <CircleDotIcon size={14} className="opacity-60" /> Niet afgerond
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Compact beschikbaarheid summary: Studiedagen status + per-group count lines,
          rendered as clean Origin UI inline alerts (shared with the Overzicht). */}
      {availabilityLines.length > 0 && (
        <div className="mt-3">
          <AvailabilityAlerts lines={availabilityLines} />
        </div>
      )}

      {!allDone ? (
        <div className="mt-4">
          <Notice tone="error" icon={<AlertCircleIcon />} title="Nog niet alle stappen zijn afgerond">
            Rond eerst alle bovenstaande stappen af voordat u de gegevens kunt aanleveren.
          </Notice>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          {/* Earlier delivery with pending edits: make clear this is a re-aanlevering. */}
          {hasChanges && flow?.lastSubmittedAt && (
            <Notice tone="warning" icon={<PencilIcon />} title="Wijzigingen nog niet ingeleverd">
              Laatst ingeleverd op {formatSubmittedAt(flow.lastSubmittedAt)}. Lever hieronder opnieuw in om uw wijzigingen door te geven.
            </Notice>
          )}

          {/* Planning is begonnen, maar aanleveren blijft mogelijk: informatieve inline
              waarschuwing (alert-warning-inline), bewust niet-blokkerend. */}
          {planningStarted && (
            <div className="rounded-md border px-4 py-3">
              <p className="text-sm">
                <TriangleAlert aria-hidden className="-mt-0.5 me-3 inline-flex text-amber-500" size={16} />
                De planning is al begonnen. U kunt nog wijzigen en opnieuw aanleveren, maar KUVO is mogelijk al met de planning gestart.
              </p>
            </div>
          )}
          {submitError && (
            submitError === "no_changes" ? (
              <Notice tone="error" icon={<AlertCircleIcon />} title="Er zijn geen wijzigingen om aan te leveren">
                Uw gegevens zijn al aangeleverd.
              </Notice>
            ) : (
              <Notice tone="error" icon={<AlertCircleIcon />} title="Het aanleveren is niet gelukt">
                Controleer uw internetverbinding en probeer het opnieuw.
              </Notice>
            )
          )}

          <div className="space-y-3">
            <label htmlFor={confirmId} className="flex gap-3">
              <Checkbox
                id={confirmId}
                checked={confirmed}
                onCheckedChange={(val) => setConfirmed(val === true)}
                className="mt-0.5 shrink-0"
              />
              <span className="text-sm leading-5">
                Ik heb de schoolgegevens nagekeken en het klopt.
              </span>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            KUVO verwerkt deze gegevens zoals beschreven in de{" "}
            <Link to="/privacyverklaring" target="_blank" className="cursor-pointer font-medium text-primary hover:underline">
              privacyverklaring
            </Link>.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? "Bezig met aanleveren..." : hasChanges ? "Wijzigingen aanleveren" : "Gegevens aanleveren"}
            </Button>
            {/* Whole-flow "Wijzigingen terugzetten": only when there is something to revert (pending
                changes) AND a version exists to revert to. Secondary, beside the primary button. */}
            {hasChanges && (
              <Button
                size="lg"
                variant="outline"
                onClick={() => setRevertTarget({ scope: "flow" })}
                disabled={submitting}
              >
                <RotateCcwIcon aria-hidden size={16} className="-ms-0.5 opacity-70" />
                Wijzigingen terugzetten
              </Button>
            )}
          </div>
        </div>
      )}

      {revertTarget && (
        <RevertChangesDialog
          open
          onOpenChange={(o) => { if (!o) setRevertTarget(null); }}
          schoolId={scId}
          flow="schoolgegevens"
          sectionCodes={revertTarget.sectionCodes}
          scope={revertTarget.scope}
          lastSubmittedAt={flow?.lastSubmittedAt ?? null}
          onReverted={handleReverted}
        />
      )}
    </div>
  );
}

function DeliveredCard({ scId, title, body }: { scId: number; title: string; body: string }) {
  return (
    <div className="rounded-lg border p-4 shadow-xs">
      <div className="flex gap-3">
        <div aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 max-md:mt-0.5">
          <CheckIcon className="text-emerald-600" size={16} />
        </div>
        <div className="flex grow flex-col gap-3">
          <div className="space-y-1">
            <p className="font-medium text-sm text-foreground">{title}</p>
            <p className="text-sm text-muted-foreground">{body}</p>
          </div>
          <div>
            <Button size="sm" render={<Link to={`/school/${scId}/programmaselectie`} />}>
              Naar programma
              <ArrowRightIcon size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
